/**
 * @copyright (C) 2023-2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2024-11-10 18:24:26
 **/
/* eslint-disable @typescript-eslint/no-explicit-any */

import i18next from "i18next";
import Backend from "i18next-http-backend";
import sprintf from "i18next-sprintf-postprocessor";
import LanguageDetector from "i18next-browser-languagedetector";
import parse from "html-react-parser";
import { marked } from "marked";
// import { initReactI18next } from "react-i18next";

export type LanguageType = "en" | "ko" | "ja" | "de" | "es" | "fr" | "it" | "zh_CN" | "zh_HK" | "zh_TW";

/**
 * Initializes the language settings using i18next with various plugins and configurations.
 *
 * @param {string | string[]} namespaces - The list of namespace identifiers to be used for language resources. Default value is ["Application", "CommonCode", "ErrorCode"].
 * @param {string} version - The version string used in query parameters for backend loading. Default value is "0.1.1".
 * @param {boolean} use_timestamp - Indicates whether to append a timestamp to prevent caching, used as a query parameter. Default value is false.
 * @param {boolean} debug - Enables or disables debug mode for i18next, affecting console logging of information, warnings, and errors. Default is false.
 * @return {Promise<void>} A promise that resolves when i18next has been initialized successfully.
 */
function initialize_language(
	namespaces: string | string[] = ["Application", "CommonCode", "ErrorCode"],
	version: string = "0.1.1",
	use_timestamp: boolean = false,
	debug = false
): Promise<void> {
	return new Promise<void>((resolve) => {
		i18next
			// .use(initReactI18next)
			.use(Backend)
			.use(sprintf)
			.use(LanguageDetector)
			.use({
				type: "logger",
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				log: (args: any) => {
					const messages = args[0].split(": ");
					console.log(`#${messages[0]}# `, messages[1], args[1]);
				},
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				warn: (args: any) => {
					const messages = args[0].split(": ");
					console.warn(`${messages[0]} `, messages[1], args[1]);
				},
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				error: (args: any) => {
					const messages = args[0].split(": ");
					console.error(`${messages[0]} `, messages[1], args[1]);
				}
			})
			.init({
				// resources,
				lng: "ko",
				fallbackLng: "en",
				supportedLngs: ["en", "ko", "ja", "de", "es", "fr", "it", "zh_CN", "zh_HK", "zh_TW"],
				load: "languageOnly",
				debug,
				ns: namespaces,
				backend: {
					loadPath: `/locales/{{ns}}.{{lng}}.json`,
					queryStringParams: {
						v: version,
						...(use_timestamp ? { timestamp: new Date().getTime().toString() } : {})
					}
				},
				overloadTranslationOptionHandler: sprintf.overloadTranslationOptionHandler
			})
			.then(() => {
				resolve();
			});
	});
}

export async function change_language(language: LanguageType) {
	await i18next.changeLanguage(language);
}

export function current_language() {
	return i18next.language;
}

export function _T(key: string, options: any = undefined) {
	try {
		let result = i18next.t(key, options) as string;
		if (result.trim().length <= 0) {
			result = key;
		}
		return result?.replace(new RegExp("\\n", "gi"), `\n`) || result;
	} catch {
		return key;
	}
}

function strip_html_entities(str: string) {
	if (str !== undefined && str !== null && str !== "") {
		str = String(str);

		str = str.replace(/<script[^>]*>([\S\s]*?)<\/script>/gim, "");
		str = str.replace(/<\/?\w(?:[^"'>]|"[^"]*"|'[^']*')*>/gim, "");
		const element = document.createElement("div");
		element.innerHTML = str;
		str = element.textContent!;
		element.textContent = "";
	}
	return str;
}

export function _P(key: string, { useMD, useStripTags, ...options }: any = { useMD: false, useStripTags: true }) {
	const contents = _T(key, options);
	if (useMD) {
		return parse(marked.parse(contents).toString());
	}
	if (useStripTags) {
		return parse(contents);
	}
	return parse(strip_html_entities(contents));
}

const Language = {
	initialize: initialize_language,
	change: change_language,
	current: current_language
};

export default Language;
